import React, {useEffect, useState} from 'react'
import {Modal, Tab, Tabs} from 'react-bootstrap'
import moment from 'moment'
import {getECommerceLead} from '../../api/e-commerce'
import {ECommerceLead, ECommerceOfferStatus} from '../../models/e-commerce'
import LeadCommentsModal from './LeadCommentsModal'

type Props = {
  show: boolean
  leadId: number | null
  leadSummary: ECommerceLead | null
  onClose: () => void
  onStatusUpdated?: (status: ECommerceOfferStatus | null) => void
}

const offerStatusLabels: Record<string, string> = {
  NEW: 'Novi lead',
  CONTACTED: 'Kontaktiran',
  WAITING: 'Čekamo odgovor',
  QUALIFIED: 'Kvalifikovan',
  PREPARING_OFFER: 'Priprema ponude',
  OFFER_SENT: 'Ponuda poslana',
  NEGOTIATION: 'Pregovori',
  WON: 'Dobijen posao',
  LOST: 'Izgubljen posao',
  NOT_RELEVANT: 'Nije relevantno',
}

const formatDate = (value?: string | null) => {
  if (!value || !moment(value).isValid()) return value || '-'
  return moment(value).format('DD-MM-YYYY HH:mm')
}

const formatValue = (value: unknown, date = false) => {
  if (value === undefined || value === null || value === '') return '-'
  if (date) return formatDate(String(value))
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value) ?? '-'
  return String(value)
}

const formatScore = (value?: number | string | null) => {
  if (value === undefined || value === null || value === '') return null
  return `${value}/100`
}

const getErrorMessage = (error: any) => {
  const status = error?.response?.status
  if (status === 401) return 'Vaša sesija je istekla. Prijavite se ponovo.'
  if (status === 403) return 'Nemate dozvolu za pregled ovog leada.'
  if (status === 404) return 'Lead nije pronađen.'
  if (status >= 500) return 'Greška servera. Pokušajte ponovo.'
  return error?.response?.data?.message ?? 'Podaci leada se nisu mogli učitati.'
}

type FieldProps = {
  label: string
  value: unknown
  date?: boolean
  href?: string
}

const DetailField: React.FC<FieldProps> = ({label, value, date = false, href}) => {
  const text = formatValue(value, date)

  return (
    <div className='col-md-6 mb-4'>
      <div className='border rounded p-3 h-100'>
        <div className='text-muted fs-7 mb-1'>{label}</div>
        {href && text !== '-' ? (
          <a href={href} target='_blank' rel='noreferrer' className='text-break'>
            {text}
          </a>
        ) : (
          <div className='text-break' style={{whiteSpace: 'pre-wrap'}}>
            {text}
          </div>
        )}
      </div>
    </div>
  )
}

const SectionTitle: React.FC<{children: React.ReactNode}> = ({children}) => (
  <h5 className='border-bottom pb-3 mb-5'>{children}</h5>
)

const LeadDetailsModal: React.FC<Props> = ({show, leadId, leadSummary, onClose, onStatusUpdated}) => {
  const [lead, setLead] = useState<ECommerceLead | null>(leadSummary)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    if (!show || !leadId) return

    let cancelled = false
    setLead(leadSummary)
    setActiveTab('overview')
    setLoading(true)
    setError(null)

    getECommerceLead(leadId)
      .then((response) => {
        if (cancelled) return
        const responseData = response.data as any
        setLead(responseData?.data ?? responseData)
      })
      .catch((requestError) => {
        if (!cancelled) setError(getErrorMessage(requestError))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [show, leadId, leadSummary])

  const companyName = lead?.company_name || lead?.official_company_name || 'Lead'
  const websiteUrl = lead?.website
    ? /^https?:\/\//i.test(lead.website)
      ? lead.website
      : `https://${lead.website}`
    : undefined
  const linkedinUrl = lead?.linkedin
    ? /^https?:\/\//i.test(lead.linkedin)
      ? lead.linkedin
      : `https://${lead.linkedin}`
    : undefined

  return (
    <Modal show={show} onHide={onClose} size='xl' dialogClassName='modal-dialog-scrollable'>
      <Modal.Header closeButton>
        <Modal.Title>Detalji potencijalnog klijenta — {companyName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className='text-center py-10'>
            <span className='spinner-border text-primary' role='status' aria-label='Učitavanje detalja' />
          </div>
        )}

        {!loading && error && <div className='alert alert-danger'>{error}</div>}

        {!loading && !error && lead && (
          <Tabs activeKey={activeTab} onSelect={(key) => key && setActiveTab(key)} className='mb-6'>
            <Tab eventKey='overview' title='Pregled'>
              <SectionTitle>Osnovni podaci</SectionTitle>
              <div className='row'>
                <DetailField label='ID' value={lead.id} />
                <DetailField label='Naziv kompanije' value={lead.company_name} />
                <DetailField label='Zvanični naziv kompanije' value={lead.official_company_name} />
                <DetailField label='Web stranica' value={lead.website} href={websiteUrl} />
                <DetailField label='Država' value={lead.country} />
                <DetailField label='Kod države' value={lead.country_code} />
                <DetailField label='Grad' value={lead.city} />
                <DetailField label='Regija' value={lead.region} />
                <DetailField label='Industrija' value={lead.industry} />
                <DetailField label='Tip potencijalnog klijenta' value={lead.lead_type} />
              </div>

              <SectionTitle>Projekat i potreba</SectionTitle>
              <div className='row'>
                <DetailField label='Naziv projekta' value={lead.project_name} />
                <DetailField label='Lokacija projekta' value={lead.project_location} />
                <DetailField label='Faza projekta' value={lead.project_stage} />
                <DetailField label='Vrijednost projekta' value={lead.project_value} />
                <DetailField label='Veličina projekta' value={lead.project_size} />
                <DetailField label='Potencijalna potreba za proizvodom' value={lead.potential_product_need} />
                <DetailField label='Status potrebe za proizvodom' value={lead.product_need_status} />
                <DetailField label='Zašto je relevantan' value={lead.why_relevant} />
              </div>
            </Tab>

            <Tab eventKey='commercial' title='Komercijala'>
              <SectionTitle>Komercijalni podaci</SectionTitle>
              <div className='row'>
                <DetailField
                  label='Status ponude'
                  value={lead.offer_status ? offerStatusLabels[lead.offer_status] ?? lead.offer_status : null}
                />
                <DetailField label='Status signala' value={lead.signal_status} />
                <DetailField label='Signal kupovine' value={lead.buying_signal} />
                <DetailField label='Datum signala' value={lead.signal_date} date />
                <DetailField label='Potencijal' value={formatScore(lead.lead_score)} />
                <DetailField label='Pouzdanost' value={formatScore(lead.confidence_score)} />
                <DetailField label='Prioritet' value={lead.priority} />
                <DetailField label='Investitor / vlasnik objekta' value={lead.investor_facility_owner} />
                <DetailField label='Glavni izvođač' value={lead.main_contractor} />
                <DetailField label='Ostali učesnici projekta' value={lead.other_project_participants} />
              </div>

              <SectionTitle>Komentari</SectionTitle>
              <LeadCommentsModal
                embedded
                commentsOnly
                show={show && activeTab === 'commercial'}
                lead={lead}
                onClose={() => undefined}
              />
            </Tab>

            <Tab eventKey='research' title='Istraživanje'>
              <SectionTitle>Istraživačke informacije</SectionTitle>
              <div className='row'>
                <DetailField label='Provjerene činjenice' value={lead.verified_facts} />
                <DetailField label='Zaključci' value={lead.inferences} />
                <DetailField label='Nepoznate informacije' value={lead.unknown_information} />
                <DetailField label='Istraživačke napomene' value={lead.research_notes} />
                <DetailField label='Izvorni URL-ovi' value={lead.source_urls} />
                <DetailField label='Dokazi izvora' value={lead.source_evidence} />
                <DetailField label='Istraživač po državi' value={lead.research_country_agent} />
                <DetailField label='ID istraživanja' value={lead.research_run_id} />
                <DetailField label='Pronađeno' value={lead.found_at} date />
                <DetailField label='Posljednja provjera' value={lead.last_checked} date />
                <DetailField label='Kreirano' value={lead.created_at} date />
                <DetailField label='Ažurirano' value={lead.updated_at} date />
              </div>
            </Tab>

            <Tab eventKey='contacts' title='Kontakti'>
              <SectionTitle>Kontakt podaci</SectionTitle>
              <div className='row'>
                <DetailField label='Kontakt osoba' value={lead.contact_person} />
                <DetailField label='Uloga kontakta' value={lead.contact_role} />
                <DetailField label='E-mail' value={lead.email} />
                <DetailField label='Telefon' value={lead.phone} />
                <DetailField label='LinkedIn' value={lead.linkedin} href={linkedinUrl} />
                <DetailField label='Opšti kontakt kompanije' value={lead.general_company_contact} />
              </div>
            </Tab>

            <Tab eventKey='commercial-comments' title='Komercijala komentari'>
              <LeadCommentsModal
                embedded
                show={show && activeTab === 'commercial-comments'}
                lead={lead}
                onClose={() => undefined}
                onStatusUpdated={(status) => {
                  setLead((currentLead) =>
                    currentLead ? {...currentLead, offer_status: status} : currentLead
                  )
                  onStatusUpdated?.(status)
                }}
              />
            </Tab>
          </Tabs>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default LeadDetailsModal
